import React from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { NumberFormatOptions } from '@formatjs/ecma402-abstract';
import { majorScale, Icon, defaultFills, minorScale } from '../../npaper';
import { Card } from '../../npaper/Card';
import { PointTooltipProps, ResponsiveLine, Serie } from '@nivo/line';
import { DatumValue } from '@nivo/core';
import moment from 'moment';

export interface IKPICardProps extends BoxProps<any> {
  options?: NumberFormatOptions;
  description: string;
  currency?: string;
  data: Serie;
}

export const KPICard: React.FC<IKPICardProps> = ({
  options = {},
  description,
  currency,
  data,
  ...props
}) => {
  const intl = useIntl();

  const value = React.useMemo(() => {
    return data.data.reduce((p, n) => p + ((n.y as number) || 0), 0);
  }, [data]);

  const positive = React.useMemo(() => {
    if (data.data.length < 2) return false;
    const preLast = data.data[data.data.length - 2];
    const last = data.data[data.data.length - 1];
    return preLast.y! < last.y!;
  }, [data]);

  return (
    <Card
      height="220px"
      paddingBottom="40px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      position="relative"
      {...props}
    >
      <Box
        is="p"
        className="font-mono"
        fontSize="28px"
        fontWeight="bold"
        marginBottom={majorScale(2)}
      >
        <FormattedNumber
          // eslint-disable-next-line react/style-prop-object
          style="decimal"
          maximumFractionDigits={0}
          minimumFractionDigits={0}
          {...options}
          value={value}
        />
        {currency && (
          <>
            {' '}
            <Box is="span" fontWeight={400}>
              {currency}
            </Box>
          </>
        )}
      </Box>
      <p>
        <Icon
          icon={positive ? 'arrow-up' : 'arrow-down'}
          color={
            positive ? defaultFills.success[500] : defaultFills.danger[500]
          }
          marginRight={majorScale(1)}
        />
        {description}
      </p>

      <Box position="absolute" bottom="0" left="0" right="0" height="100px">
        <ResponsiveLine
          data={[data]}
          curve="catmullRom"
          colors={
            data.color
              ? data.color
              : positive
              ? defaultFills.success[500]
              : defaultFills.danger[500]
          }
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          xFormat={(v: DatumValue) => moment(v).format('L')}
          yFormat={(v: DatumValue) =>
            intl.formatNumber(v as number, {
              format: !!currency ? 'currency' : 'short',
              currency: currency,
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })
          }
          margin={{
            top: majorScale(2),
            bottom: majorScale(1),
            right: majorScale(2),
            left: majorScale(2),
          }}
          axisTop={null}
          axisBottom={null}
          axisLeft={null}
          axisRight={null}
          enableGridX={false}
          enableGridY={false}
          pointSize={4}
          pointLabelYOffset={-12}
          enableCrosshair={false}
          useMesh={true}
          legends={[]}
          tooltip={Tooltip}
        />
      </Box>
    </Card>
  );
};

const Tooltip = ({ point }: PointTooltipProps) => (
  <Card padding={minorScale(1)} background="white" elevation={2} border="none">
    <p className="font-mono">{point.data.yFormatted}</p>
    <p className="small muted">{point.data.xFormatted}</p>
  </Card>
);
